import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { CheckCircle2, XCircle, ArrowLeft, RotateCw, FileDown } from "lucide-react";
import { toast } from "sonner";

export default function ResultPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [attempt, setAttempt] = useState(null);

  useEffect(() => {
    api.get(`/me/attempts/${id}`).then((r) => setAttempt(r.data));
  }, [id]);

  const isFinal = attempt?.test_kind === "final";

  const downloadCertificate = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/me/attempts/${id}/certificate`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(isFinal ? "No se pudo descargar el diploma" : "No se pudo descargar el certificado");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${isFinal ? "diploma" : "certificado"}-${attempt.test_title.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!attempt) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Cargando…</div>;
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/dashboard">
          <Button variant="ghost" className="mb-6 text-gray-600" data-testid="back-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al panel
          </Button>
        </Link>

        <Card
          className={`p-10 border shadow-none rounded-lg text-center fade-up ${
            attempt.passed ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
          }`}
          data-testid="result-banner"
        >
          {attempt.passed ? (
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-600" strokeWidth={1.5} />
          ) : (
            <XCircle className="w-16 h-16 mx-auto text-red-500" strokeWidth={1.5} />
          )}
          <h1 className="font-display text-5xl mt-4 text-gray-900">{attempt.score_percentage}%</h1>
          <p className={`mt-2 font-medium ${attempt.passed ? "text-emerald-700" : "text-red-700"}`}>
            {attempt.passed ? "¡Aprobado!" : `Suspendido. Necesitas ${attempt.pass_percentage}% para aprobar.`}
          </p>
          <p
            className={`mt-4 max-w-2xl mx-auto text-sm leading-relaxed ${
              attempt.passed ? "text-gray-700" : "text-red-700"
            }`}
            data-testid="result-message"
          >
            {attempt.passed && attempt.score_percentage >= 100
              ? "¡Enhorabuena, vaya notaza! Se nota que has estudiado."
              : attempt.passed
              ? "Has tenido algún pequeño error, pero aún así has aprobado. Recuerda que puedes repetir la prueba para mejorar tu nota y que, si tienes alguna duda, siempre puedes llamarnos para repasar el tema con nosotros o apuntarte a alguna de nuestras tutorías."
              : "¡Vaya! Has tenido demasiadas respuestas incorrectas. Repasa de nuevo la teoría y vuelve a intentarlo. Si tienes dudas puedes apuntarte a nuestras tutorías o puedes llamarnos para repasar el tema juntos y resolver tus dudas."}
          </p>
          <p className="text-xs text-gray-500 mt-3">
            {attempt.correct_count} de {attempt.total_questions} correctas · {attempt.duration_seconds}s
          </p>

          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            {attempt.passed && !user?.is_apl && !user?.is_tk ? (
              <Button
                className="bg-amber-500 hover:bg-amber-600"
                onClick={downloadCertificate}
                data-testid="download-certificate"
              >
                <FileDown className="w-4 h-4 mr-2" />
                {isFinal ? "Descargar diploma" : "Descargar certificado"}
              </Button>
            ) : attempt.passed && (user?.is_apl || user?.is_tk) ? (
              <p
                className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-4 py-2"
                data-testid="apl-manual-issuance-notice"
              >
                {isFinal ? "El diploma" : "El certificado"} se emite manualmente por el centro. Recibirás una notificación cuando esté listo.
              </p>
            ) : (
              <Button
                className="bg-brand hover:bg-brand-hover text-white"
                onClick={() => navigate(`/test/${attempt.test_id}`)}
                data-testid="retry-test"
              >
                <RotateCw className="w-4 h-4 mr-2" />
                Reintentar
              </Button>
            )}
          </div>
        </Card>

        <h2 className="font-display text-2xl text-gray-900 mt-10 mb-4">Revisión</h2>
        <div className="space-y-3">
          {attempt.breakdown.map((b, i) => (
            <Card
              key={i}
              className={`p-4 border shadow-none rounded-lg ${b.is_correct ? "border-emerald-200" : "border-red-200"}`}
            >
              <div className="flex items-start gap-3">
                {b.is_correct ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {i + 1}. {b.question_text}
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    Tu respuesta: <span className="font-mono-data">[{b.user_answer.join(", ")}]</span>
                    {!b.is_correct && (
                      <>
                        {" · "}Correcta:{" "}
                        <span className="font-mono-data text-emerald-700">
                          [{b.correct_answer.join(", ")}]
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
